import { useQuery, UseQueryOptions, QueryKey } from '@tanstack/react-query'
import qs from 'query-string'

import axios from '@pepper/utils/src/function/axios/editorInstance'
import extract from '@pepper/utils/src/function/extract'
import {
  getIsPublicDocFromAPIType,
  getURLFromAPIType
} from '@pepper/utils/src/function/getEditorApiEndpoint'

import {
  EditorApps,
  InterlinkingSuggestionItem
} from '@pepper/utils/src/types/editor'
import {
  ContentAuditReport,
  ContentAuditReportType,
  AIDetectionAuditReport,
  PlagiarismEstimateData,
  ReadabilityAppData
} from '@pepper/utils/src/types/content_audit'
import type {
  GapReportWithDocument,
  GapReportWithDocumentV3,
  ResearchReport
} from '@pepper/utils/src/types/seo'

interface ParameterV2 {
  data: {
    docId: string
    isPublic?: boolean
  } & Metadata
  options?: Pick<
    UseQueryOptions<Response>,
    'enabled' | 'refetchInterval' | 'refetchOnWindowFocus' | 'staleTime'
  >
}

interface Parameter {
  data: {
    itemId: string
    editorType: PepperDocTypes
  } & Metadata
  options?: Pick<
    UseQueryOptions<Response>,
    'enabled' | 'refetchInterval' | 'refetchOnWindowFocus' | 'staleTime'
  >
}

type Metadata =
  | {
      app: EditorApps.CONTENT_GAP_REPORT | EditorApps.CONTENT_RESEARCH_REPORT
      metadata: {
        /** add a check in enabled option if passing undefined value */
        id: string | undefined
      }
    }
  | {
      app: EditorApps.CONTENT_AUDIT
      metadata: {
        id?: string
        report?: ContentAuditReportType
      }
    }
  | {
      app: EditorApps.INTERLINKING
      metadata: {
        id?: undefined
        report: ['interlinking-suggestions']
      }
    }
  | {
      app: EditorApps.READABILITY
      metadata: {
        id?: string
        wordCount: number
      }
    }
  | {
      app: EditorApps.AI_DETECTION
      metadata: {
        id?: string
      }
    }

type Response =
  | ResearchReport
  | GapReportWithDocument
  | GapReportWithDocumentV3
  | ReadabilityAppData
  | ContentAuditReport
  | PlagiarismEstimateData
  | AIDetectionAuditReport
  | InterlinkingSuggestionItem[]

const STALE_TIME = 5 * 60 * 1000 // 5 mins

export async function getEditorAppContentV2(data: ParameterV2['data']) {
  const url = qs.stringifyUrl(
    {
      url: `/v2/documents/${data.docId}/apps/content`,
      query: {
        app: data.app,
        async: data.app === EditorApps.CONTENT_AUDIT ? true : undefined,
        'metadata[id]': data.metadata?.id,
        'metadata[report]':
          data.app === EditorApps.INTERLINKING
            ? data.metadata.report
            : undefined,
        is_public: data.isPublic ?? false
      }
    },
    { skipNull: true, skipEmptyString: true }
  )
  const response = await axios.get(url)
  return extract.data<Response>(response).data
}

export async function getEditorAppContent(data: Parameter['data']) {
  const type = getURLFromAPIType(data.editorType)

  const url = qs.stringifyUrl(
    {
      url: `/${type}/${data.itemId}/editor/apps/content`,
      query: {
        app: data.app,
        async: data.app === EditorApps.CONTENT_AUDIT ? true : undefined,
        'metadata[id]': data.metadata?.id,
        'metadata[report]':
          data.app === EditorApps.CONTENT_AUDIT && data.metadata.report,
        is_public: getIsPublicDocFromAPIType(data.editorType)
      }
    },
    { skipNull: true, skipEmptyString: true }
  )

  const response = await axios.get(url, { params: true })
  return extract.data<Response>(response).data
}

export function useGetEditorAppContent({ data, options }: Parameter) {
  return useQuery({
    queryKey: ['editor', 'apps', data.app, data.metadata] as QueryKey,
    queryFn: () => getEditorAppContent(data),
    staleTime: STALE_TIME,
    ...options
  })
}

export function useGetEditorAppContentV2({ data, options }: ParameterV2) {
  return useQuery({
    queryKey: ['editor', 'apps', data.app, data.metadata] as QueryKey,
    queryFn: () => getEditorAppContentV2(data),
    staleTime: STALE_TIME,
    select: response => {
      if (data.app === EditorApps.INTERLINKING) {
        const _response = response as InterlinkingSuggestionItem[]
        // id is not present in the response, so adding it manually
        return _response.map((item, index) => ({ ...item, id: index + 1 }))
      }
      return response
    },
    ...options
  })
}
